import React from 'react';
import { AlertCircle, Shield, Heart, BookOpen } from 'lucide-react';

const Disclaimer = () => {
  return (
    <div className="disclaimer-page">
      {/* Hero Section */}
      <section className="bg-cream py-16">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-center mb-6">
            <Shield size={48} className="text-golden mr-4" />
            <h1 className="text-5xl font-bold text-dark-text" data-testid="disclaimer-heading">
              Medical Disclaimer
            </h1>
          </div>
          <p className="text-xl text-center text-gray-700 max-w-3xl mx-auto">
            Important information about Amrita Sparsha products and services
          </p>
        </div>
      </section>

      {/* Main Disclaimer Content */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {/* Primary Disclaimer */}
            <div className="bg-amber-50 border-l-4 border-golden p-6 mb-8" data-testid="primary-disclaimer">
              <div className="flex items-start">
                <AlertCircle size={24} className="text-golden mr-4 flex-shrink-0 mt-1" />
                <div>
                  <h2 className="text-2xl font-bold text-dark-text mb-3">Important Notice</h2>
                  <p className="text-gray-700 leading-relaxed text-lg">
                    Amrita Sparsha products are <strong>supportive wellness formulations</strong>. 
                    They do not replace professional medical consultation. For chronic or persistent 
                    concerns, consultation with a qualified physician is recommended.
                  </p>
                </div>
              </div>
            </div>

            {/* Detailed Sections */}
            <div className="space-y-8">
              {/* Product Information */}
              <div className="" data-testid="product-information-section">
                <div className="flex items-center mb-4">
                  <Heart size={28} className="text-golden mr-3" />
                  <h2 className="text-3xl font-bold text-dark-text">Product Information</h2>
                </div>
                <div className="bg-gray-50 p-6 rounded-lg">
                  <ul className="space-y-3 text-gray-700 leading-relaxed">
                    <li className="flex items-start">
                      <span className="text-golden mr-2 font-bold">•</span>
                      <span>Amrita Sparsha products are designed to <strong>support skin, hair, and personal wellness</strong> as part of a balanced care routine.</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-golden mr-2 font-bold">•</span>
                      <span>Products are <strong>not intended to diagnose, treat, cure, or prevent any disease</strong> or medical condition.</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-golden mr-2 font-bold">•</span>
                      <span>Individual results may vary based on personal health conditions, lifestyle, and usage patterns.</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-golden mr-2 font-bold">•</span>
                      <span>All product descriptions and benefits listed are for <strong>supportive care purposes only</strong>.</span>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Medical Consultation */}
              <div className="" data-testid="consultation-section">
                <div className="flex items-center mb-4">
                  <BookOpen size={28} className="text-golden mr-3" />
                  <h2 className="text-3xl font-bold text-dark-text">When to Consult a Doctor</h2>
                </div>
                <div className="bg-gray-50 p-6 rounded-lg">
                  <p className="text-gray-700 leading-relaxed mb-4">
                    Professional medical consultation is recommended in the following situations:
                  </p>
                  <ul className="space-y-3 text-gray-700 leading-relaxed">
                    <li className="flex items-start">
                      <span className="text-golden mr-2 font-bold">•</span>
                      <span>Chronic or persistent health concerns</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-golden mr-2 font-bold">•</span>
                      <span>Acute or severe symptoms requiring immediate medical attention</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-golden mr-2 font-bold">•</span>
                      <span>Pre-existing medical conditions or ongoing treatments</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-golden mr-2 font-bold">•</span>
                      <span>Pregnancy, nursing, or pediatric care needs</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-golden mr-2 font-bold">•</span>
                      <span>Before starting any new wellness regimen alongside existing medications</span>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Professional Practice */}
              <div className="" data-testid="professional-practice-section">
                <div className="flex items-center mb-4">
                  <Shield size={28} className="text-golden mr-3" />
                  <h2 className="text-3xl font-bold text-dark-text">Our Professional Standards</h2>
                </div>
                <div className="bg-gray-50 p-6 rounded-lg">
                  <ul className="space-y-3 text-gray-700 leading-relaxed">
                    <li className="flex items-start">
                      <span className="text-golden mr-2 font-bold">•</span>
                      <span>Dr. Sanjay Panicker and Amrita Homeopathy follow <strong>ethical, evidence-aware homeopathic practice</strong>.</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-golden mr-2 font-bold">•</span>
                      <span>All clinical consultations are conducted with patient safety and realistic expectations as priorities.</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-golden mr-2 font-bold">•</span>
                      <span>Products are formulated following traditional principles and modern safety standards.</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-golden mr-2 font-bold">•</span>
                      <span>We maintain transparency about product capabilities and limitations.</span>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Usage Guidelines */}
              <div className="" data-testid="usage-guidelines-section">
                <div className="flex items-center mb-4">
                  <AlertCircle size={28} className="text-golden mr-3" />
                  <h2 className="text-3xl font-bold text-dark-text">Safe Usage Guidelines</h2>
                </div>
                <div className="bg-gray-50 p-6 rounded-lg">
                  <ul className="space-y-3 text-gray-700 leading-relaxed">
                    <li className="flex items-start">
                      <span className="text-golden mr-2 font-bold">•</span>
                      <span>Always read product labels and follow usage instructions carefully.</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-golden mr-2 font-bold">•</span>
                      <span>Perform a patch test before using any new topical product.</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-golden mr-2 font-bold">•</span>
                      <span>Discontinue use if irritation or adverse reactions occur and consult a healthcare professional.</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-golden mr-2 font-bold">•</span>
                      <span>Store products as directed, away from direct sunlight and out of reach of children.</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-golden mr-2 font-bold">•</span>
                      <span>Check expiration dates and do not use expired products.</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Contact for Medical Concerns */}
            <div className="mt-12 bg-cream p-8 rounded-lg text-center">
              <h3 className="text-2xl font-bold text-dark-text mb-4" data-testid="contact-medical-heading">
                Have Medical Questions?
              </h3>
              <p className="text-gray-700 mb-6">
                For personalized medical advice or to discuss your specific health concerns, 
                please book a consultation with Dr.Sanjay Panicker.
              </p>
              <a 
                href="/contact" 
                className="btn-primary px-8 py-4 rounded-lg font-semibold text-lg inline-block"
                data-testid="disclaimer-contact-button"
              >
                Book Consultation
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Footer Note */}
      <section className="py-12 bg-gray-100">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center text-sm text-gray-600">
            <p>
              <strong>Last Updated:</strong> {new Date().toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
            <p className="mt-2">
              This disclaimer is subject to change. Please review it periodically for updates.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Disclaimer;